import {dehydrate, QueryClient} from '@tanstack/react-query';
import {GetStaticPathsContext, GetStaticPropsContext} from 'next';

import {
    CurriculumSubject,
    getAcademyFromService,
    getAllAcademiesForLocaleFromService,
} from '@udemy/career-academies';
import {RENDER_MODES} from '@udemy/nextjs-core';
import {udSentry} from '@udemy/sentry';
import {udApi} from '@udemy/ud-api';

import {enrichAcademyData} from 'src/features/career-academy-landing-page/utils/enrich-academy-data';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {FOUR_HOURS} from 'src/lib/constants';
import {getEnrichedTopics} from 'src/lib/get-enriched-topics';
import {fetcher} from 'src/lib/gql-fetcher';
import {AppContextData} from 'src/types/page-props';

import {getLayout} from '../../components/layout';
import {
    CareerAcademyLandingPageFeature,
    CareerAcademyLandingPageFeatureProps,
} from '../../features/career-academy-landing-page';
import {getStaticAppContext} from '../api/context.api';
import {getCourseData} from './lib/get-course-data';

export async function getStaticPaths({}: GetStaticPathsContext) {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

export async function getStaticProps({locale, params}: GetStaticPropsContext) {
    locale = locale as string;
    const translations = await getCompactTranslations(locale);
    const client = new QueryClient();

    const academySlug = params?.academy as string;

    // Resolve appContext
    const [staticData] = await Promise.all([getStaticAppContext({locale})]);
    const appContext: AppContextData = {
        ...staticData,
        // application specific context can go here
    };

    const academyData = await getAcademyFromService(academySlug, locale, {
        customGqlFetcher: fetcher,
    });

    if (!academyData) {
        return {
            notFound: true,
        };
    }

    let courses = undefined;
    try {
        const coursesResult = await Promise.allSettled([
            getCourseData(academyData.curriculum.buyableIds.map(Number), locale),
        ]);

        if (coursesResult[0].status === 'fulfilled') {
            courses = coursesResult[0].value;
        } else {
            udSentry.captureException(coursesResult[0].reason);
        }
    } catch (e) {
        udSentry.captureException(e);
    }

    const topics = await getEnrichedTopics(academyData.secondaryTopics, locale);

    try {
        const curriculumSubjectsWithPracticeTests: Record<string, CurriculumSubject[]> = {};
        for (const [courseId, curriculumSubject] of Object.entries(
            academyData.curriculum.subjects,
        )) {
            if (
                courses?.find((course) => course?.id?.toString() === courseId)
                    ?.is_practice_test_course
            ) {
                const response = await udApi.get(
                    `/courses/${courseId}/public-curriculum-sections/?locale=${locale}`,
                );
                const practiceTestCurriculum = response.data.results[0].items;
                curriculumSubjectsWithPracticeTests[courseId] = practiceTestCurriculum.map(
                    (section: {title: string; content_summary: string}) => ({
                        title: section.title,
                        summaryItems: [section.content_summary],
                    }),
                );
            } else {
                curriculumSubjectsWithPracticeTests[courseId] = curriculumSubject;
            }
        }

        academyData.curriculum.subjects = curriculumSubjectsWithPracticeTests;
    } catch (e) {
        udSentry.captureException(e);
    }

    const academiesListForLocale = await getAllAcademiesForLocaleFromService(locale, {
        customGqlFetcher: fetcher,
        pageOptions: {limit: 4},
    });
    const inDemandAcademiesList = await Promise.all(
        academiesListForLocale
            .filter((academy) => !!academy)
            .filter((academyFromList) => academyData.slug !== academyFromList.slug)
            .slice(0, 3)
            .map((academy) => enrichAcademyData(academy, locale)),
    );

    const staticProps = {
        props: {
            appContext,
            dehydratedState: dehydrate(client),
            locale,
            pageKey: 'career_accelerator_landing_page',
            renderMode: RENDER_MODES.ISR, // supports render functions in _document.tsx and _app.tsx.
            translations,
            academy: academyData,
            topics,
        },
        revalidate: FOUR_HOURS,
    };

    if (courses) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (staticProps.props as any).courses = courses;
    }

    if (topics) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (staticProps.props as any).topics = topics;
    }

    if (inDemandAcademiesList) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (staticProps.props as any).inDemandAcademiesList = inDemandAcademiesList;
    }

    return staticProps;
}

export default function CareerAcademyLandingPage(props: CareerAcademyLandingPageFeatureProps) {
    return <CareerAcademyLandingPageFeature {...props} />;
}

CareerAcademyLandingPage.getLayout = getLayout;
